// frontend/src/pages/admin/CVGeneration/CVGenerationTab.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  FileText, Filter, Download, Eye, 
  History, X
} from 'lucide-react';
import { StatusBadge } from '../../../components/UI/StatusBadge';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import PillSelect from '../../../components/UI/PillSelect';
import FormSelect from '../../../components/UI/FormSelect';
import { 
  useActiveCVTemplates, 
  useGenerateCV, 
  useGenerateBatchCVs,
  usePreviewCVHTML,
  useCVGenerationLogs,
  useBatchDataQuality
} from '../../../hooks/useCVData';
import { staffAPI } from '../../../services/api';
import { formatDateTime, getTodayInSingaporeTimezone } from '../../../utils/dateUtils';
import toast from '../../../utils/toast';
import Portal from '../../../components/UI/Portal';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';
import EmployeeListPanel from './EmployeeListPanel';
import CVPreviewPanel from './CVPreviewPanel';

const CVGenerationTab = () => {
  // State
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]); // Array of staff_ids
  const [previewEmployeeId, setPreviewEmployeeId] = useState(null);
  const [tenderReference, setTenderReference] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    staff_id: '',
    template_id: '',
    status: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  // State for staff list
  const [staffList, setStaffList] = useState([]);
  const [staffListLoading, setStaffListLoading] = useState(true);

  // Queries
  const { data: templates = [], isLoading: templatesLoading } = useActiveCVTemplates();
  const generateCV = useGenerateCV();
  const generateBatchCVs = useGenerateBatchCVs();
  
  // Get preview HTML when employee and template are selected
  const { 
    data: previewHTMLData, 
    isLoading: previewLoading,
    refetch: refetchPreviewHTML
  } = usePreviewCVHTML(previewEmployeeId, selectedTemplate?.id);

  // Get data quality for all employees
  const { data: qualityData = { employees: [] } } = useBatchDataQuality([]);

  // Generation logs for history
  const { 
    data: generationLogs = { results: [], count: 0 }, 
    isLoading: logsLoading 
  } = useCVGenerationLogs({
    page: currentPage,
    page_size: pageSize,
    ...filters,
  });

  // Fetch ALL staff (paginate through all pages)
  useEffect(() => {
    const fetchAllStaff = async () => {
      try {
        setStaffListLoading(true);
        let allStaff = [];
        let page = 1;
        let hasMore = true;
        
        // Fetch all pages
        while (hasMore) {
          const response = await staffAPI.getStaffList({ 
            page_size: 200,
            page: page,
            employment_status: 'active',
          });
          const results = response.data?.results || response.data || [];
          allStaff = [...allStaff, ...results];
          
          // Check if there are more pages
          hasMore = response.data?.next !== null && results.length > 0;
          page++;
          
          // Safety limit to prevent infinite loops
          if (page > 20) break;
        }
        
        // Map to consistent format and filter out system accounts
        const mappedStaff = allStaff
          .filter(s => !['A0001', 'S0000'].includes(s.staff_id))
          .map(s => ({
            staff_id: s.staff_id,
            full_name: s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim(),
            designation: s.designation || '',
            department: s.department?.department_name || s.department || 'Unassigned',
          }));
        
        setStaffList(mappedStaff);
      } catch (error) {
        console.error('Error fetching staff list:', error);
        toast.error('Failed to load staff list');
      } finally {
        setStaffListLoading(false);
      }
    };
    fetchAllStaff();
  }, []);

  // Auto-select first template when templates load
  useEffect(() => {
    if (templates.length > 0 && !selectedTemplate) {
      setSelectedTemplate(templates[0]);
    }
  }, [templates, selectedTemplate]);

  // Get the previewing employee object
  const previewEmployee = useMemo(() => {
    if (!previewEmployeeId) return null;
    return staffList.find(e => e.staff_id === previewEmployeeId) || null;
  }, [previewEmployeeId, staffList]);

  // Get quality info for previewing employee
  const previewQualityInfo = useMemo(() => {
    if (!previewEmployeeId || !qualityData.employees) return null;
    return qualityData.employees.find(e => e.staff_id === previewEmployeeId) || null;
  }, [previewEmployeeId, qualityData.employees]);

  // Handlers
  const handleSelectEmployee = useCallback((staffId) => {
    setSelectedEmployees(prev => {
      if (prev.includes(staffId)) {
        return prev.filter(id => id !== staffId);
      } else {
        return [...prev, staffId];
      }
    });
  }, []);

  const handleToggleSelectAll = useCallback((staffIds, selectAll) => {
    if (selectAll) {
      setSelectedEmployees(prev => {
        const newSet = new Set([...prev, ...staffIds]);
        return Array.from(newSet);
      });
    } else {
      setSelectedEmployees(prev => prev.filter(id => !staffIds.includes(id)));
    }
  }, []);

  const handlePreviewEmployee = useCallback((staffId) => {
    setPreviewEmployeeId(staffId);
  }, []);

  const handleGenerateSelected = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a CV template');
      return;
    }

    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    try {
      if (selectedEmployees.length === 1) {
        await generateCV.mutateAsync({
          staff_id: selectedEmployees[0],
          template_id: selectedTemplate.id,
          tender_reference: tenderReference || undefined,
        });
      } else {
        await generateBatchCVs.mutateAsync({
          staff_ids: selectedEmployees,
          template_id: selectedTemplate.id,
          tender_reference: tenderReference || undefined,
        });
      }
      // Clear selection after successful generation
      setSelectedEmployees([]);
    } catch (error) {
      // Error is handled by the mutation hooks
    }
  };

  const handleDownloadSingle = async (staffId) => {
    if (!selectedTemplate) {
      toast.error('Please select a CV template');
      return;
    }

    try {
      // Generate and download in one step
      const result = await generateCV.mutateAsync({
        staff_id: staffId,
        template_id: selectedTemplate.id,
        tender_reference: tenderReference || undefined,
      });
      
      // If generation was successful and we have a log ID, download it
      if (result.data?.cv_log_id) {
        const response = await staffAPI.downloadCV(result.data.cv_log_id);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        // Generate standardized filename: CV_[StaffID]_[FullName]_[Date].pdf
        const employee = staffList.find(e => e.staff_id === staffId);
        const safeName = (employee?.full_name || 'Unknown').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_');
        const dateStr = getTodayInSingaporeTimezone();
        link.download = `CV_${staffId}_${safeName}_${dateStr}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleDownloadFromHistory = async (logId, staffId, staffName = '') => {
    try {
      // Log warning if critical data is missing (for debugging)
      if (!staffId || !staffName) {
        console.warn('CV Download: Missing staff data', { logId, staffId, staffName });
      }
      
      const response = await staffAPI.downloadCV(logId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Generate standardized filename: CV_[StaffID]_[FullName]_[Date].pdf
      const safeName = (staffName || 'Unknown').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_');
      const dateStr = getTodayInSingaporeTimezone();
      link.download = `CV_${staffId || 'employee'}_${safeName}_${dateStr}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('CV downloaded successfully');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to download CV';
      console.error('CV Download Error:', { logId, staffId, error: errorMessage, fullError: error });
      toast.error(errorMessage);
    }
  };

  const [isDownloadingDocx, setIsDownloadingDocx] = useState(false);
  const [isRegeneratingPreview, setIsRegeneratingPreview] = useState(false);

  const handleDownloadDocx = async (staffId) => {
    if (!selectedTemplate) {
      toast.error('Please select a CV template');
      return;
    }

    setIsDownloadingDocx(true);
    try {
      const response = await staffAPI.downloadCVAsDocx({
        staff_id: staffId,
        template_id: selectedTemplate.id,
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Generate standardized filename
      const employee = staffList.find(e => e.staff_id === staffId);
      const safeName = (employee?.full_name || 'Unknown').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_');
      const dateStr = getTodayInSingaporeTimezone();
      link.download = `CV_${staffId}_${safeName}_${dateStr}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('DOCX downloaded successfully');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to download DOCX';
      toast.error(errorMessage);
    } finally {
      setIsDownloadingDocx(false);
    }
  };

  // Regenerate preview with proper error handling and debouncing
  const handleRegeneratePreview = useCallback(async (staffId) => {
    if (!selectedTemplate) {
      toast.error('Please select a CV template');
      return;
    }

    if (!staffId) {
      toast.error('No employee selected');
      return;
    }

    // Prevent concurrent regenerations
    if (isRegeneratingPreview) {
      return;
    }

    setIsRegeneratingPreview(true);
    try {
      // Force fresh fetch (bypass cache, cancel in-flight requests)
      await refetchPreviewHTML({ 
        cancelRefetch: true, // Cancel any in-flight requests
        throwOnError: true 
      });
      toast.success('Preview refreshed');
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 404) {
        toast.error('Employee or template not found');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view this CV');
      } else if (error.response?.status === 429) {
        toast.error('Too many requests. Please wait a moment and try again.');
      } else {
        const errorMessage = error.response?.data?.error || error.message || 'Failed to refresh preview';
        toast.error(errorMessage);
      }
    } finally {
      setIsRegeneratingPreview(false);
    }
  }, [selectedTemplate, refetchPreviewHTML, isRegeneratingPreview]);

  // Debounce regenerate function to prevent spam
  const debounceTimerRef = useRef(null);
  const debouncedRegeneratePreview = useCallback((staffId) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Debounce with 500ms delay
    debounceTimerRef.current = setTimeout(() => {
      handleRegeneratePreview(staffId);
    }, 500);
  }, [handleRegeneratePreview]);

  // Cleanup: Cancel debounce timer and in-flight requests on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // React Query will handle request cancellation automatically
    };
  }, []);

  const clearFilters = () => {
    setFilters({
      staff_id: '',
      template_id: '',
      status: '',
    });
    setCurrentPage(1);
  };


  const totalPages = Math.ceil((generationLogs.count || 0) / pageSize);
  const isGenerating = generateCV.isPending || generateBatchCVs.isPending;

  return (
    <div className="space-y-4">
      {/* Header Bar - Template & Tender Reference */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Template Selection */}
          <div className="flex-1 min-w-[200px] max-w-xs">
            <label className="block text-xs font-medium text-gray-500 mb-1">CV Template</label>
            {templatesLoading ? (
              <div className="h-10 bg-gray-100 rounded-lg animate-pulse"></div>
            ) : (
              <FormSelect
                options={templates.length === 0
                  ? [{ value: '', label: 'No templates available' }]
                  : templates.map(t => ({ value: t.id, label: `${t.template_name} (v${t.template_version || 1})` }))
                }
                value={selectedTemplate?.id || ''}
                onChange={(val) => {
                  const template = templates.find(t => t.id === parseInt(val));
                  setSelectedTemplate(template || null);
                }}
                theme="admin"
              />
            )}
          </div>

          {/* Tender Reference */}
          <div className="flex-1 min-w-[200px] max-w-xs">
            <label className="block text-xs font-medium text-gray-500 mb-1">Tender Reference (Optional)</label>
            <input
              type="text"
              className="input-admin w-full"
              placeholder="e.g., T2024-001"
              value={tenderReference}
              onChange={(e) => setTenderReference(e.target.value)}
            />
          </div>

          {/* History Button */}
          <div className="flex items-end">
            <button
              onClick={() => setShowHistory(true)}
              className="icon-button-admin"
              title="View Generation History"
              aria-label="View Generation History"
            >
              <History className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Split Panel Layout */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="grid grid-cols-12 divide-x divide-gray-200" style={{ height: 'calc(100dvh - 280px)', minHeight: '500px' }}>
          {/* Left Panel - Employee List (3/12) — wide enough for name scan */}
          <div className="col-span-12 md:col-span-3 overflow-hidden">
            <EmployeeListPanel
              employees={staffList}
              selectedEmployees={selectedEmployees}
              previewEmployeeId={previewEmployeeId}
              onSelectEmployee={handleSelectEmployee}
              onToggleSelectAll={handleToggleSelectAll}
              onPreviewEmployee={handlePreviewEmployee}
              isLoading={staffListLoading}
              qualityData={qualityData}
            />
          </div>

          {/* Right Panel - CV Preview (9/12) */}
          <div className="col-span-12 md:col-span-9 overflow-hidden">
            <CVPreviewPanel
              employee={previewEmployee}
              previewHTML={previewLoading ? '' : (previewHTMLData?.html || '')}
              qualityInfo={previewQualityInfo}
              isLoading={previewLoading}
              isGenerating={isGenerating}
              isDownloadingDocx={isDownloadingDocx}
              isRegenerating={isRegeneratingPreview}
              selectedCount={selectedEmployees.length}
              onDownload={handleDownloadSingle}
              onDownloadDocx={handleDownloadDocx}
              onRegenerate={debouncedRegeneratePreview}
              onGenerateSelected={handleGenerateSelected}
              templateName={selectedTemplate?.template_name}
            />
          </div>
        </div>
      </div>

      {/* History Modal */}
      {showHistory && (
        <Portal>
          <div className="modal-overlay" onClick={() => setShowHistory(false)}>
            <div className="modal-container modal-container-xl" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">CV Generation History</h3>
                <button onClick={() => setShowHistory(false)} className="modal-close-admin">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Filters */}
              <div className="border-b p-4 bg-gray-50">
                <div className="flex items-center gap-4 flex-wrap">
                  <DebouncedSearchInput
                    value={filters.staff_id}
                    onSearch={(val) => setFilters(prev => ({ ...prev, staff_id: val }))}
                    placeholder="Staff ID..."
                    className="max-w-[200px]"
                  />
                  <PillSelect
                    options={[
                      { value: '', label: 'All Statuses' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'processing', label: 'Processing' },
                      { value: 'failed', label: 'Failed' },
                    ]}
                    value={filters.status}
                    onChange={(val) => setFilters(prev => ({ ...prev, status: val }))}
                    theme="admin"
                  />
                  <button
                    onClick={clearFilters}
                    className="btn btn-admin-outline btn-sm"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="modal-content p-0">
                {logsLoading ? (
                  <div className="px-4 py-8 text-center">
                    <LoadingSpinner size="md" />
                  </div>
                ) : generationLogs.results?.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-gray-500">
                    No generation history found
                  </div>
                ) : (
                  <>
                    {/* Mobile card view */}
                    <div className="md:hidden max-h-[400px] overflow-y-auto p-4 space-y-3">
                      {generationLogs.results?.map((log) => (
                        <div key={log.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{log.staff_name || 'N/A'}</p>
                              <p className="text-xs text-gray-500 truncate">{log.staff_id || ''}</p>
                            </div>
                            <div className="flex-shrink-0">
                              <StatusBadge status={log.cv_generation_status || 'pending'} />
                            </div>
                          </div>
                          <div className="mt-3 space-y-1.5 flex-1">
                            <div className="flex items-start justify-between gap-3 text-sm">
                              <span className="text-gray-500 flex-shrink-0">Template</span>
                              <span className="text-gray-900 text-right truncate min-w-0">{log.template_name || 'N/A'}</span>
                            </div>
                            <div className="flex items-start justify-between gap-3 text-sm">
                              <span className="text-gray-500 flex-shrink-0">Generated</span>
                              <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{formatDateTime(log.cv_generated_at)}</span>
                            </div>
                            <div className="flex items-start justify-between gap-3 text-sm">
                              <span className="text-gray-500 flex-shrink-0">Tender Ref</span>
                              <span className="text-gray-900 text-right truncate min-w-0">{log.cv_tender_reference || '—'}</span>
                            </div>
                          </div>
                          {log.cv_generation_status === 'completed' && (
                            <div className="card-action-footer justify-end gap-2">
                              <button
                                onClick={() => handleDownloadFromHistory(log.id, log.staff_id, log.staff_name)}
                                className="icon-button-admin"
                                title="Download CV"
                                aria-label="Download CV"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Desktop table view */}
                    <div className="hidden md:block overflow-x-auto max-h-[400px]">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Employee</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Template</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Generated</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Tender Ref</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {generationLogs.results?.map((log) => (
                            <tr key={log.id} className="table-row">
                              <td className="px-4 py-3 text-sm">
                                <div className="font-medium text-gray-900">
                                  {log.staff_name || 'N/A'}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {log.staff_id || ''}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {log.template_name || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <StatusBadge status={log.cv_generation_status || 'pending'} />
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {formatDateTime(log.cv_generated_at)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {log.cv_tender_reference || '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-right">
                                {log.cv_generation_status === 'completed' && (
                                  <button
                                    onClick={() => handleDownloadFromHistory(log.id, log.staff_id, log.staff_name)}
                                    className="icon-button-admin"
                                    title="Download CV"
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-4 py-3 bg-gray-50 border-t">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, generationLogs.count || 0)} of {generationLogs.count || 0}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="btn btn-admin-outline btn-sm"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-600">
                          {currentPage} / {totalPages}
                        </span>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="btn btn-admin-outline btn-sm"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button onClick={() => setShowHistory(false)} className="btn btn-admin-outline">
                  Close
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};

export default CVGenerationTab;
